import { get } from '@atlassian/jira-common-rest/src/internal/latest/frontendconfig/properties';
import { toErrorId, toErrorMessage } from '@atlassian/jira-project-settings-apps-common/src/types';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error';
import type { BaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import { PACKAGE_NAME } from '../../model/constants';
import type { InstanceProperties } from './types';

export const instancePropertiesOptions = {
	instanceProperties: [
		'jira.projectkey.maxlength',
		'jira.projectname.maxlength',
		'jira.option.allowunassigned',
	],
	consumerMeta: {
		id: 'fetchInstanceProperties',
		packageName: PACKAGE_NAME,
		teamName: 'morphinecake',
	},
};

/**
 * This service fetches configurable instance properties that are required to validate project details. In our case
 * project key and project name max length. Jira admins can modify these values via system configuration
 * (/jira/settings/system/general-configuration).
 */
const InstancePropertiesServiceImpl = {
	get: (baseUrl: BaseUrl): Promise<InstanceProperties> => {
		const getInstancePropertiesPromise = famaCatchAndLogFetchError(
			get(baseUrl, instancePropertiesOptions),
			PACKAGE_NAME,
			toErrorId('getInstanceProperties'),
			toErrorMessage('Failed to fetch instance properties for project details'),
		);

		return getInstancePropertiesPromise.then((response) => {
			if (response.instance) {
				const projectNameMaxLength = parseInt(response.instance['jira.projectname.maxlength'], 10);
				const projectKeyMaxLength = parseInt(response.instance['jira.projectkey.maxlength'], 10);
				const allowUnassigned = Boolean(response.instance['jira.option.allowunassigned']);
				return {
					projectNameMaxLength: Number.isFinite(projectNameMaxLength)
						? projectNameMaxLength
						: undefined,
					projectKeyMaxLength: Number.isFinite(projectKeyMaxLength)
						? projectKeyMaxLength
						: undefined,
					allowUnassigned,
				};
			}

			return {
				projectNameMaxLength: undefined,
				projectKeyMaxLength: undefined,
				allowUnassigned: undefined,
			};
		});
	},
} as const;

export default InstancePropertiesServiceImpl;
